import { useCallback, useEffect, useState } from 'react';

import { errorKey, isPending, type AuthApi, type Product,
         type PurchaseOrder, type Session } from '../auth/api';
import { useI18n } from '../i18n/I18nProvider';
import { Button } from '../ui/Button';
import { PaySheet } from '../shop/PaySheet';
import { ProductCard } from '../shop/ProductCard';
import type { GameApi, GameProfile } from '../profile/api';
import '../shop/shop.css';

/**
 * 充值。
 *
 * **商品从服务端来**（`GET /shop/products`），前端不写死任何一档 ——
 * 运营要能改价、上下架、开特卖，那些不该是一次发版。
 *
 * 列表是**按人算的**：限购剩余次数因人而异，所以不能缓存成全局的一份。
 */
export function DepositTab(
  { api, game, session, onWatch, onNeedLogin }:
  {
    api: AuthApi;
    /** 币种和余额从这条链路来（game-server 是钱包的单写者） */
    game: GameApi;
    session: Session | null;
    /**
     * 把一笔待付订单交给 App 层盯着。
     *
     * **不是由这个页面自己盯**：用户下完单会切出去付款，回来时可能已经
     * 不在充值页了 —— 甚至可能在游戏里。到账这件事得有人在更外面说。
     */
    onWatch(orderNo: string): void;
    onNeedLogin(): void;
  },
) {
  const { t, i18n } = useI18n();
  const [items, setItems] = useState<Product[] | null>(null);
  const [gp, setGp] = useState<GameProfile | null>(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  //: 待支付的那张单。**不是 null 就把收款面板打开。**
  const [pay, setPay] = useState<PurchaseOrder | null>(null);

  // **两边各拉各的**（和个人页同一个做法）：商品在 account，
  // 币种和余额在 game-server。哪边挂了就少那一块，不是整页空白。
  const load = useCallback(async () => {
    if (!session) return;
    const [a, g] = await Promise.allSettled([
      api.products(session.token), game.profile(session.token)]);
    if (a.status === 'fulfilled') setItems(a.value.items);
    if (g.status === 'fulfilled') setGp(g.value);
    const bad = [a, g].find((r) => r.status === 'rejected');
    setErr(bad ? t(errorKey((bad as PromiseRejectedResult).reason)) : '');
  }, [api, game, session, t]);

  useEffect(() => { void load(); }, [load]);

  const money = (n: number) =>
    (gp ? i18n.money(n, gp.cur, { symbol: gp.sym, decimals: gp.dec })
        : i18n.num(n));

  async function buy(code: string) {
    if (!session) return;
    setBusy(code);
    setErr('');
    setOk('');
    try {
      const o = await api.buy(session.token, code);
      if (isPending(o.status)) {
        // 真渠道：单子停在 created，把收款面板打开让他去付。
        // **这是常态，不是失败** —— 只有 dev 渠道才会当场 credited。
        setPay(o);
        onWatch(o.orderNo);        // 交给 App 层盯着，关了面板也还盯着
      } else {
        setOk(t('shop.getCoins', { v: money(o.coins) }));
      }
      await load();          // 限购次数和余额都变了
    } catch (e) {
      setErr(t(errorKey(e)));
      await load();          // 可能是别处买满了，把最新状态拉回来
    } finally {
      setBusy('');
    }
  }

  if (!session) {
    return (
      <section className="stack">
        <h1 className="h1">{t('tab.deposit')}</h1>
        <div className="placeholder">
          <p className="small placeholder__note">{t('settings.notLoggedIn')}</p>
          <Button variant="primary" onClick={onNeedLogin}>
            {t('action.loginOrRegister')}
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="stack">
      <h1 className="h1">{t('tab.deposit')}</h1>

      {/* 余额放在最上面：充值页的第一个问题就是"我现在有多少" */}
      <div className="card spread">
        <span className="small dim">{t('profile.balance')}</span>
        <span className="h2 num">{gp ? money(gp.bal) : '—'}</span>
      </div>

      {ok && <p className="shop__ok" role="status">{ok}</p>}
      {err && <p className="field__err" role="alert">{err}</p>}

      {items === null && !err && <p className="small dim">{t('action.working')}</p>}
      {items?.length === 0 && <p className="small dim">{t('shop.empty')}</p>}

      {items && items.length > 0 && (
        <div className="shop__grid">
          {items.map((p) => (
            <ProductCard key={p.code} p={p} money={money} busy={busy === p.code}
                         onBuy={() => void buy(p.code)} />
          ))}
        </div>
      )}

      {/* **如实说当前渠道是什么。** dev 渠道点一下就到账，
          不说清楚的话，等真接了支付要付钱时会觉得是被坑了。 */}
      <p className="small dim">{t('shop.devPay')}</p>

      <PaySheet open={pay !== null} order={pay} api={api} session={session}
                money={money}
                onClose={() => { setPay(null); void load(); }}
                onPaid={(o) => {
                  setOk(t('shop.getCoins', { v: money(o.coins) }));
                  void load();          // 到账了，余额和限购次数都变了
                }} />
    </section>
  );
}
